import React, { useState } from 'react';
import { TinyCommunity } from 'types/company/community';
import SectionHeading from 'ui/elements/SectionHeading';
import AddAction from 'ui/elements/actions/AddAction';
import Card from 'ui/views/cards/Card';
import { useCompanyNotes, useCreateNote } from './hooks';
import CreateNoteForm from './CreateNoteForm';
import NoteItem from './NoteItem';
import { CreateCommunityCompanyNoteDTO, CommunityCompanyNoteDTO } from 'types/company/communityNotes';
import useNotify from 'hooks/useNotify';
import useCommunityRole from 'hooks/useCommunityRole';
import Resource from 'util/resource/Resource';
import EyeIcon from 'ui/elements/icons/EyeIcon';

interface Props {
  community: TinyCommunity;
  companyId: number;
}

export default function CommunityNotes({ community, companyId }: Props) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);
  const notify = useNotify();
  const access = useCommunityRole(community.slug);

  const canWrite = access.hasScope('Write', 'Community.CompanyNotes');

  const { resource } = useCompanyNotes(community.id, companyId);

  const [createNote, isCreatingNote] = useCreateNote(community.id, companyId, {
    onSuccess: (createdNote: CommunityCompanyNoteDTO) => {
      notify('success', 'Note created successfully');
      setShowCreateForm(false);
      setExpandedNoteId(createdNote.id);
    },
    onFailure: () => {
      notify('error', 'Failed to create note');
    },
  });

  const handleCreateNote = async (data: CreateCommunityCompanyNoteDTO) => {
    await createNote(data);
  };

  return (
    <Resource resource={resource}>
      {notes => (
        <>
          <SectionHeading heading="Notes" addMobileMargin>
            {canWrite && (
              <button onClick={() => setShowCreateForm(!showCreateForm)}>
                <AddAction>New note</AddAction>
              </button>
            )}
          </SectionHeading>
          <Card>
            {showCreateForm && canWrite && (
              <CreateNoteForm
                onSubmit={handleCreateNote}
                onCancel={() => setShowCreateForm(false)}
                isSaving={isCreatingNote}
              />
            )}
            {notes.length === 0 && !showCreateForm ? (
              <p className="u-text-center u-content-spacing-y text-italic">No notes have been added yet.</p>
            ) : (
              notes.map((note: CommunityCompanyNoteDTO, index: number) => (
                <NoteItem
                  key={note.id}
                  note={note}
                  community={community}
                  companyId={companyId}
                  isExpanded={note.id === expandedNoteId}
                  index={index}
                />
              ))
            )}

            <p className="text-metadata u-content-spacing-top">
              <EyeIcon className="u-quarter-spacing-right" /> Notes are visible to community admins and employees.
            </p>
          </Card>
        </>
      )}
    </Resource>
  );
}
